import { Alert, Card, Col, DatePicker, Empty, Row, Statistic, Table, Typography } from 'antd';
import type { TableProps } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { financeApi, type StatementLine } from '@/api/finance';
import { PageHeader } from '@/shared/components/page-header';
import { ExportButton } from '@/shared/components/ui';

const PKR = (n: string | number) => Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

const sectionColumns = (label: string): TableProps<StatementLine>['columns'] => [
  {
    title: 'Code',
    dataIndex: 'accountCode',
    width: 90,
    render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text>,
  },
  { title: label, dataIndex: 'accountName' },
  {
    title: 'Amount',
    dataIndex: 'amount',
    width: 160,
    align: 'right',
    render: (v: string) => <Typography.Text strong>₨ {PKR(v)}</Typography.Text>,
  },
];

export function FinanceBalanceSheetPage() {
  const [asOf, setAsOf] = useState<Dayjs>(dayjs());

  const bs = useQuery({
    queryKey: ['finance', 'balance-sheet', asOf.format('YYYY-MM-DD')],
    queryFn: () => financeApi.balanceSheet(asOf.format('YYYY-MM-DD')),
  });

  const data = bs.data;
  const variance = data ? Number(data.variance) : 0;
  const balanced = Math.abs(variance) < 0.01;

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <PageHeader
        eyebrow="Finance / Reports"
        title="Balance sheet"
        description="Snapshot of assets, liabilities, and equity as of a given date. Net surplus to date is added to the equity section so the balance equation always holds."
        actions={
          <ExportButton
            items={[{
              kind: 'xlsx',
              url: '/reports/exports/balance-sheet.xlsx',
              params: { asOf: asOf.format('YYYY-MM-DD') },
              fileName: `balance-sheet-${asOf.format('YYYY-MM-DD')}.xlsx`,
              disabled: !data,
            }]}
          />
        }
      />

      <Card>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={6}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              As of
            </Typography.Text>
            <DatePicker style={{ width: '100%' }} value={asOf} onChange={(v) => v && setAsOf(v)} allowClear={false} />
          </Col>
        </Row>
      </Card>

      {data && (
        <Card>
          <Row gutter={[16, 12]}>
            <Col xs={12} md={6}>
              <Statistic title="Total assets" value={`₨ ${PKR(data.totalAssets)}`} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Total liabilities" value={`₨ ${PKR(data.totalLiabilities)}`} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Total equity" value={`₨ ${PKR(data.totalEquity)}`} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title={balanced ? 'Balanced' : 'Out of balance'}
                value={`₨ ${PKR(data.variance)}`}
                styles={{ content: { color: balanced ? '#389e0d' : '#cf1322' } }}
              />
            </Col>
          </Row>
          {!balanced && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              message="Balance sheet is out of balance"
              description={`Total assets (₨ ${PKR(data.totalAssets)}) ≠ Total liabilities + equity (₨ ${PKR(
                data.totalLiabilitiesAndEquity,
              )}). Check for missing GL postings on cash-moving events, or an out-of-period entry that doesn't roll up to a posting account.`}
            />
          )}
        </Card>
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title={<Typography.Text strong>Assets</Typography.Text>}>
            {data && data.assets.length === 0 ? (
              <Empty description="No assets" />
            ) : (
              <Table<StatementLine>
                rowKey={(r) => `${r.accountCode}-${r.accountName}`}
                dataSource={data?.assets ?? []}
                columns={sectionColumns('Account')}
                pagination={false}
                size="small"
                summary={() =>
                  data && (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} />
                      <Table.Summary.Cell index={1}>
                        <Typography.Text strong>Total assets</Typography.Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <Typography.Text strong>₨ {PKR(data.totalAssets)}</Typography.Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )
                }
              />
            )}
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title={<Typography.Text strong>Liabilities & Equity</Typography.Text>}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Liabilities
            </Typography.Text>
            {data && data.liabilities.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No liabilities" />
            ) : (
              <Table<StatementLine>
                rowKey={(r) => `L-${r.accountCode}-${r.accountName}`}
                dataSource={data?.liabilities ?? []}
                columns={sectionColumns('Account')}
                pagination={false}
                size="small"
                summary={() =>
                  data && (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} />
                      <Table.Summary.Cell index={1}>
                        <Typography.Text strong>Total liabilities</Typography.Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <Typography.Text strong>₨ {PKR(data.totalLiabilities)}</Typography.Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )
                }
              />
            )}
            <div style={{ height: 14 }} />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Equity
            </Typography.Text>
            <Table<StatementLine>
              rowKey={(r) => `E-${r.accountCode}-${r.accountName}`}
              dataSource={data?.equity ?? []}
              columns={sectionColumns('Account')}
              pagination={false}
              size="small"
              summary={() =>
                data && (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} />
                    <Table.Summary.Cell index={1}>
                      <Typography.Text strong>Total equity</Typography.Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <Typography.Text strong>₨ {PKR(data.totalEquity)}</Typography.Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )
              }
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
